import { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileCheck2,
  FileSpreadsheet,
  FileText,
  RotateCcw,
  ShieldCheck,
  Upload
} from "lucide-react";
import {
  CHECK_STATUSES,
  compare5746Records,
  download5746ComparisonExcel,
  readExemptionReportFile,
  readSgkServicePdfFile
} from "../helpers/postDeclaration5746Check";

const STATUS_COLORS = {
  [CHECK_STATUSES.MATCHED]: { background: "#dcfce7", color: "#166534", border: "#86efac" },
  [CHECK_STATUSES.DAY_DIFFERENCE]: { background: "#fef3c7", color: "#92400e", border: "#fcd34d" },
  [CHECK_STATUSES.MISSING_IN_SGK]: { background: "#fee2e2", color: "#991b1b", border: "#fca5a5" },
  [CHECK_STATUSES.MISSING_IN_REPORT]: { background: "#ffedd5", color: "#9a3412", border: "#fdba74" },
  [CHECK_STATUSES.REVIEW]: { background: "#e0e7ff", color: "#3730a3", border: "#a5b4fc" }
};

const fileName = (value) => String(value || "").replace(/^.*[\\/]/, "");

export default function PostDeclaration5746CheckView({ themeMode = "light" }) {
  const dark = themeMode === "dark";
  const [reportData, setReportData] = useState(null);
  const [sgkData, setSgkData] = useState(null);
  const [reportFileName, setReportFileName] = useState("");
  const [sgkFileName, setSgkFileName] = useState("");
  const [manualMatches, setManualMatches] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [busySource, setBusySource] = useState("");
  const [error, setError] = useState("");
  const colors = {
    text: dark ? "#e5edf6" : "#123f50",
    muted: dark ? "#9fb0c4" : "#64748b",
    surface: dark ? "#162133" : "#fff",
    subtle: dark ? "#203047" : "#f8fafc",
    border: dark ? "#334155" : "#dbe2ea",
    input: dark ? "#111827" : "#fff"
  };

  const comparison = useMemo(() => {
    if (!reportData || !sgkData) return null;
    return compare5746Records({
      reportRecords: reportData.records,
      sgkRecords: sgkData.records,
      manualMatches
    });
  }, [manualMatches, reportData, sgkData]);

  const visibleRows = useMemo(() => {
    if (!comparison) return [];
    return statusFilter === "all"
      ? comparison.rows
      : comparison.rows.filter((row) => row.status === statusFilter);
  }, [comparison, statusFilter]);

  const loadReport = async (file) => {
    if (!file) return;
    setBusySource("report");
    setError("");
    try {
      const parsed = await readExemptionReportFile(file);
      setReportData(parsed);
      setReportFileName(fileName(file.name));
      setManualMatches({});
    } catch (reason) {
      setReportData(null);
      setReportFileName("");
      setError(reason.message || "Excel dosyası okunamadı.");
    } finally {
      setBusySource("");
    }
  };

  const loadSgkPdf = async (file) => {
    if (!file) return;
    setBusySource("sgk");
    setError("");
    try {
      const parsed = await readSgkServicePdfFile(file);
      setSgkData(parsed);
      setSgkFileName(fileName(file.name));
      setManualMatches({});
    } catch (reason) {
      setSgkData(null);
      setSgkFileName("");
      setError(reason.message || "SGK PDF dosyası okunamadı.");
    } finally {
      setBusySource("");
    }
  };

  const setManualMatch = (sgkId, reportId) => {
    setManualMatches((current) => {
      const next = Object.fromEntries(
        Object.entries(current).filter(([, assignedReportId]) =>
          reportId === "__none__" || assignedReportId !== reportId
        )
      );
      if (reportId) next[sgkId] = reportId;
      else delete next[sgkId];
      return next;
    });
  };

  const updateSgkDay = (sgkId, value) => {
    const parsed = value === "" ? null : Math.max(0, Math.min(31, Math.round(Number(value))));
    setSgkData((current) => ({
      ...current,
      records: current.records.map((record) =>
        record.id === sgkId
          ? { ...record, days: Number.isFinite(parsed) ? parsed : null, confidence: "high" }
          : record
      )
    }));
  };

  const reset = () => {
    setReportData(null);
    setSgkData(null);
    setReportFileName("");
    setSgkFileName("");
    setManualMatches({});
    setStatusFilter("all");
    setError("");
  };

  const warnings = [...(reportData?.warnings || []), ...(sgkData?.warnings || [])];

  return (
    <section style={{ color: colors.text }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 18 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ width: 48, height: 48, borderRadius: 15, display: "grid", placeItems: "center", background: "linear-gradient(135deg,#124254,#0f766e)", color: "#fff" }}>
            <FileCheck2 size={24}/>
          </span>
          <div>
            <h2 style={{ margin: 0 }}>Beyan Sonrası 5746 Kontrolü</h2>
            <p style={{ margin: "5px 0 0", color: colors.muted, fontWeight: 650 }}>
              Muafiyetli gün raporunu SGK 05746 - 05510 hizmet listesiyle karşılaştırın
            </p>
          </div>
        </div>
        {(reportData || sgkData) && (
          <button type="button" onClick={reset} style={secondaryButton(colors)}>
            <RotateCcw size={16}/> Yeni kontrol
          </button>
        )}
      </div>

      <div style={{ ...noticeStyle(dark), marginBottom: 16 }}>
        <ShieldCheck size={18}/>
        <span><strong>Yerel ve güvenli işlem:</strong> Excel ve PDF içeriği tarayıcınızda işlenir; bu kontrol sırasında dosyalar sunucuya yüklenmez.</span>
      </div>

      {error && <div style={{ padding: 13, borderRadius: 13, marginBottom: 16, background: "#fee2e2", color: "#991b1b", fontWeight: 750 }}>{error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 14, marginBottom: 18 }}>
        <UploadCard
          title="Bizim Rapor Excel’i"
          description="Muafiyetli Gün Sayısı Raporu (.xlsx / .xls)"
          fileName={reportFileName}
          count={reportData?.records.length}
          total={reportData?.totalDays}
          accept=".xlsx,.xls"
          busy={busySource === "report"}
          icon={<FileSpreadsheet size={25}/>} 
          colors={colors}
          onFile={loadReport}
        />
        <UploadCard
          title="SGK Hizmet Listesi PDF’i"
          description="Kanun: 05746 - 05510 hizmet listesi (.pdf)"
          fileName={sgkFileName}
          count={sgkData?.records.length}
          total={sgkData?.totalDays}
          accept="application/pdf,.pdf"
          busy={busySource === "sgk"}
          icon={<FileText size={25}/>} 
          colors={colors}
          onFile={loadSgkPdf}
        />
      </div>

      {warnings.length > 0 && (
        <div style={{ padding: 13, borderRadius: 13, marginBottom: 16, background: dark ? "#3a2e18" : "#fffbeb", border: "1px solid #fcd34d", color: dark ? "#fde68a" : "#92400e" }}>
          <strong style={{ display: "flex", gap: 7, alignItems: "center" }}><AlertTriangle size={17}/> Kontrol notları</strong>
          <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
            {warnings.slice(0, 8).map((warning) => <li key={warning} style={{ marginTop: 4 }}>{warning}</li>)}
          </ul>
        </div>
      )}

      {comparison && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginBottom: 16 }}>
            <MetricCard label="Bizim toplam 5746" value={comparison.totals.reportDays} suffix="gün" colors={colors}/>
            <MetricCard label="SGK toplam" value={comparison.totals.sgkDays} suffix="gün" colors={colors}/>
            <MetricCard label="Toplam fark" value={comparison.totals.reportDays - comparison.totals.sgkDays} suffix="gün" colors={colors}/>
            <MetricCard label="Uyumlu" value={comparison.counts[CHECK_STATUSES.MATCHED] || 0} colors={colors} accent="#16a34a"/>
            <MetricCard label="Kontrol gereken" value={comparison.rows.length - (comparison.counts[CHECK_STATUSES.MATCHED] || 0)} colors={colors} accent="#dc2626"/>
          </div>

          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 18, overflow: "hidden" }}>
            <div style={{ padding: 14, borderBottom: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                <button type="button" onClick={() => setStatusFilter("all")} style={filterButton(statusFilter === "all", colors)}>Tümü ({comparison.rows.length})</button>
                {Object.values(CHECK_STATUSES).map((status) => (
                  <button key={status} type="button" onClick={() => setStatusFilter(status)} style={filterButton(statusFilter === status, colors)}>
                    {status} ({comparison.counts[status] || 0})
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => download5746ComparisonExcel({ comparison, reportFileName, sgkFileName })}
                style={primaryButton}
              >
                <Download size={17}/> Sonucu Excel indir
              </button>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", minWidth: 1120, borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: colors.subtle }}>
                    {[
                      "Durum", "Bizim rapordaki personel", "SGK’daki personel", "TC / maskeli TC",
                      "Bizim 5746", "SGK Gün", "Fark", "Eşleşme yöntemi", "Eşleştirmeyi düzelt"
                    ].map((heading) => <th key={heading} style={headerCell}>{heading}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <tr key={row.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                      <td style={bodyCell}><StatusBadge status={row.status}/></td>
                      <td style={bodyCell}><strong>{row.reportPerson || "—"}</strong><small style={subText(colors)}>{row.reportTc}</small></td>
                      <td style={bodyCell}><strong>{row.sgkPerson || "—"}</strong><small style={subText(colors)}>{row.sgkTc}</small></td>
                      <td style={bodyCell}>{row.reportTc || row.sgkTc || "—"}</td>
                      <td style={{ ...bodyCell, textAlign: "center", fontWeight: 900 }}>{row.reportDays ?? "—"}</td>
                      <td style={bodyCell}>
                        {row.sgkRecord ? (
                          <input
                            type="number"
                            min="0"
                            max="31"
                            value={row.sgkDays ?? ""}
                            onChange={(event) => updateSgkDay(row.sgkRecord.id, event.target.value)}
                            title="PDF’den okunan gün değerini gerektiğinde düzeltin"
                            style={{ width: 64, padding: "7px 8px", borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.input, color: colors.text, fontWeight: 850 }}
                          />
                        ) : "—"}
                      </td>
                      <td style={{ ...bodyCell, textAlign: "center", fontWeight: 900, color: row.difference ? "#dc2626" : colors.text }}>{row.difference ?? "—"}</td>
                      <td style={bodyCell}>{row.method || "—"}</td>
                      <td style={bodyCell}>
                        {row.sgkRecord ? (
                          <select
                            value={manualMatches[row.sgkRecord.id] || row.reportRecord?.id || "__none__"}
                            onChange={(event) => setManualMatch(row.sgkRecord.id, event.target.value)}
                            style={{ width: "100%", minWidth: 190, padding: 8, borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.input, color: colors.text }}
                          >
                            <option value="__none__">Bizim raporda yok</option>
                            {reportData.records.map((record) => (
                              <option key={record.id} value={record.id}>{record.personel || record.tcKimlikNo} · {record.days ?? "?"} gün</option>
                            ))}
                          </select>
                        ) : <span style={{ color: colors.muted }}>SGK kaydı yok</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!comparison && !error && (
        <div style={{ padding: 34, textAlign: "center", borderRadius: 18, border: `1px dashed ${colors.border}`, color: colors.muted }}>
          <Upload size={30}/>
          <p style={{ marginBottom: 0 }}>Karşılaştırmayı başlatmak için iki dosyayı da yükleyin.</p>
        </div>
      )}
    </section>
  );
}

function UploadCard({ title, description, fileName: selectedName, count, total, accept, busy, icon, colors, onFile }) {
  const [dragging, setDragging] = useState(false);
  return (
    <label
      onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        onFile(event.dataTransfer.files?.[0]);
      }}
      style={{
        display: "block",
        padding: 18,
        borderRadius: 18,
        border: `2px dashed ${dragging ? "#0f766e" : colors.border}`,
        background: dragging ? (colors.surface === "#fff" ? "#ecfdf5" : "#123f38") : colors.surface,
        cursor: busy ? "wait" : "pointer",
        boxShadow: "0 8px 24px rgba(15,23,42,.05)"
      }}
    >
      <input type="file" accept={accept} disabled={busy} onChange={(event) => onFile(event.target.files?.[0])} style={{ display: "none" }}/>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <span style={{ width: 46, height: 46, borderRadius: 14, display: "grid", placeItems: "center", background: colors.subtle, color: "#0f766e" }}>{icon}</span>
        <div style={{ minWidth: 0 }}>
          <strong style={{ display: "block" }}>{title}</strong>
          <small style={{ display: "block", marginTop: 5, color: colors.muted }}>{description}</small>
          <span style={{ display: "inline-flex", marginTop: 13, padding: "8px 11px", borderRadius: 9, background: "#0f766e", color: "#fff", fontWeight: 850, fontSize: 12 }}>
            {busy ? "Okunuyor…" : selectedName ? "Dosyayı değiştir" : "Dosya seç veya sürükle"}
          </span>
        </div>
      </div>
      {selectedName && (
        <div style={{ marginTop: 13, paddingTop: 11, borderTop: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", gap: 10, color: colors.muted, fontSize: 12 }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}><CheckCircle2 size={14} style={{ verticalAlign: "-2px", marginRight: 5, color: "#16a34a" }}/>{selectedName}</span>
          <strong style={{ color: colors.text, whiteSpace: "nowrap" }}>{count} personel · {total} gün</strong>
        </div>
      )}
    </label>
  );
}

function MetricCard({ label, value, suffix = "", colors, accent = "#0f766e" }) {
  return (
    <div style={{ padding: 15, borderRadius: 15, background: colors.surface, border: `1px solid ${colors.border}`, borderLeft: `4px solid ${accent}` }}>
      <small style={{ color: colors.muted }}>{label}</small>
      <strong style={{ display: "block", marginTop: 5, fontSize: 22 }}>{Number(value || 0).toLocaleString("tr-TR")} {suffix}</strong>
    </div>
  );
}

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || STATUS_COLORS[CHECK_STATUSES.REVIEW];
  return <span style={{ display: "inline-flex", padding: "5px 8px", borderRadius: 999, background: color.background, color: color.color, border: `1px solid ${color.border}`, fontWeight: 850, fontSize: 11, whiteSpace: "nowrap" }}>{status}</span>;
}

const primaryButton = { border: 0, borderRadius: 10, padding: "10px 13px", background: "#0f766e", color: "#fff", fontWeight: 900, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7 };
const secondaryButton = (colors) => ({ border: `1px solid ${colors.border}`, borderRadius: 10, padding: "9px 12px", background: colors.surface, color: colors.text, fontWeight: 850, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7 });
const filterButton = (active, colors) => ({ border: `1px solid ${active ? "#0f766e" : colors.border}`, borderRadius: 999, padding: "7px 10px", background: active ? "#0f766e" : colors.subtle, color: active ? "#fff" : colors.text, fontWeight: 800, fontSize: 11, cursor: "pointer" });
const noticeStyle = (dark) => ({ display: "flex", alignItems: "center", gap: 9, padding: 13, borderRadius: 13, background: dark ? "#123f38" : "#ecfdf5", border: `1px solid ${dark ? "#1f766e" : "#a7f3d0"}`, color: dark ? "#a7f3d0" : "#166534" });
const headerCell = { padding: "11px 10px", textAlign: "left", color: "#64748b", fontSize: 11, fontWeight: 900, whiteSpace: "nowrap" };
const bodyCell = { padding: "11px 10px", verticalAlign: "middle" };
const subText = (colors) => ({ display: "block", marginTop: 4, color: colors.muted, fontSize: 11 });
